// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AlertCircleFill from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleFill";
import AlertTriangleFill from "@store/Library/Components/Toolkit/Icons/AlertTriangle/AlertTriangleFill";
import CheckmarkCircle2Fill from "@store/Library/Components/Toolkit/Icons/CheckmarkCircle2/CheckmarkCircle2Fill";
import InfoFill from "@store/Library/Components/Toolkit/Icons/Info/InfoFill";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import { DataOption, DataOptions } from "@toolkit/Framework/Common/Utilities/dataOptions";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { forwardRef, ForwardRefRenderFunction, HTMLAttributes, PropsWithChildren, useMemo } from "react";
import { css } from "styled-components";

export type InfosBoxTypeOption = DataOption;
export interface InfosBoxTypeOptionMapper {
    info: InfosBoxTypeOption;
    error: InfosBoxTypeOption;
    success: InfosBoxTypeOption;
    warning: InfosBoxTypeOption;
}
export type InfosBoxTypeOptions = DataOptions<InfosBoxTypeOption> & InfosBoxTypeOptionMapper;
export type InfosBoxType = keyof InfosBoxTypeOptionMapper;

export const infosBoxTypeOptions: InfosBoxTypeOptions = {
    info: { id: "info", displayName: "Info", isDefault: true },
    error: { id: "error", displayName: "Error" },
    success: { id: "success", displayName: "Success" },
    warning: { id: "warning", displayName: "Warning" },
};

export type InfosBoxSizeOption = DataOption;
export interface InfosBoxSizeOptionMapper {
    medium: InfosBoxSizeOption;
    small: InfosBoxSizeOption;
}
export type InfosBoxSizeOptions = DataOptions<InfosBoxSizeOption> & InfosBoxSizeOptionMapper;
export type InfosBoxSize = keyof InfosBoxSizeOptionMapper;

export const infosBoxSizeOptions: InfosBoxSizeOptions = {
    medium: { id: "medium", displayName: "Medium", isDefault: true },
    small: { id: "small", displayName: "Small" },
};

export interface InfosBoxProps extends ExpandStyles<InfosBoxStyles>, HTMLAttributes<HTMLElement> {
    type?: InfosBoxType;
    size?: InfosBoxSize;
    showIcon?: boolean;
}

export interface InfosBoxBorderStyles {
    color?: string;
    radius?: string;
    style?: string;
    width?: string;
}

export interface InfosBoxStyles {
    padding?: string;
    color?: string;
    backgroundColor?: string;
    wrapper?: InjectableCss;
    icon?: IconPresentationProps;
    content?: InjectableCss;
    border?: InfosBoxBorderStyles;
}

export const infosBoxStyles: InfosBoxStyles = {
    wrapper: {
        css: css`
            ${spacingStyles()}
            width: 100%;
            display: flex;
            align-items: center;
            ${x => cssProp("line-height", x.theme.design.typography.lineHeight.semi)}
            border-radius: 4px;

            ul,
            ol,
            p {
                ${spacingStyles("0.5em", "0.5em")}
            }

            li {
                ${spacingStyles(null, "0.25em")}
            }
        `,
    },
    content: {
        css: css`
            width: 100%;
        `,
    },
    icon: {
        color: "currentColor",
    },
};

export const infosBoxTypes: Partial<Record<InfosBoxType, InfosBoxStyles>> = {
    info: {
        color: "neutral" /* Store Custom Code */,
        backgroundColor: "neutral.light" /* Store Custom Code */,
        icon: {
            src: InfoFill,
        },
    },
    warning: {
        color: "warning.contrast" /* Store Custom Code */,
        backgroundColor: "warning.light",
        icon: {
            src: AlertTriangleFill,
        },
    },
    error: {
        color: "danger.contrast" /* Store Custom Code */,
        backgroundColor: "danger.light",
        icon: {
            src: AlertTriangleFill /* Store Custom Code */,
        },
    },
    success: {
        color: "success.contrast" /* Store Custom Code */,
        backgroundColor: "success.light",
        icon: {
            src: CheckmarkCircle2Fill,
        },
    },
};

export const infosBoxSizes: Partial<Record<InfosBoxSize, InfosBoxStyles>> = {
    small: {
        padding: "0.6em",
        icon: {
            size: 24,
            css: css`
                margin-right: 0.4em;
            `,
        },
        wrapper: {
            css: css`
                ${x => cssProp("font-size", x.theme.design.typography.size.smaller)}
            `,
        },
    },
    medium: {
        padding: "1em",
        icon: {
            size: 28,
            css: css`
                margin-right: 0.6667em;
            `,
        },
        wrapper: {
            css: css`
                ${x => cssProp("font-size", x.theme.design.typography.size.small)}
            `,
        },
    },
};

const InfosBox: ForwardRefRenderFunction<HTMLDivElement, PropsWithChildren<InfosBoxProps>> = (
    { type = "info", size = "medium", showIcon = true, children, extendedStyles, overridedStyles, ...htmlAttributes },
    ref,
) => {
    const definitions = useMergeProps(infosBoxSizes[size], infosBoxTypes[type]);
    const stylesAndDefinitions = useMergeProps(infosBoxStyles, definitions);
    const baseStyles = useExpandProps(stylesAndDefinitions, extendedStyles, overridedStyles);
    const styles = useMemo(() => {
        return mergeProps<InfosBoxStyles>(baseStyles, {
            wrapper: {
                css: css`
                    ${({ theme }) => css`
                        ${cssProp("padding", baseStyles.padding)}
                        ${cssProp("color", baseStyles.color, x => resolveColor(x, theme))}
                        ${cssProp("background-color", baseStyles.backgroundColor, x => resolveColor(x, theme))}
                        ${cssProp("border-width", baseStyles.border?.width)}
                        ${cssProp("border-style", baseStyles.border?.style)}
                        ${cssProp("border-color", baseStyles.border?.color, x => resolveColor(x, theme))}
                        ${cssProp("border-radius", baseStyles.border?.radius)}
                    `}
                `,
            },
        });
    }, [baseStyles]);

    if (!children) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} {...htmlAttributes} ref={ref}>
            {showIcon && <Icon {...styles.icon} />}
            <StyledWrapper {...styles.content}>{children}</StyledWrapper>
        </StyledWrapper>
    );
};

export default forwardRef(InfosBox);
